import React, { useEffect, useState, useCallback } from 'react';
import { Flex, View, Button } from '@smarterservices/smarter-ui';
import SinglePane from '../SinglePane';
import { BlockRenderer, Blocks, Elements } from '@shared/components/BlockKit';
import Loader from '../Common/Loader';
import UninstallButton from './UninstallButton';
import useAppStore from '../../stores/app';

/**
 * SessionCompleted - A component that displays a completion message and summary after a session ends.
 * 
 * This component fetches session data and renders it using BlockKit components. It handles both
 * string and structured content, and provides a way to notify the parent component when the
 * completion flow is finished. It can also display celebratory confetti and an optional
 * extension uninstall button if configured in the stepData.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.step - The current step object containing journey information
 * @param {Object} props.stepData - Configuration and content for the completion step
 * @param {string|Object} props.stepData.body - The content to display, can be a string or BlockKit JSON
 * @param {boolean} [props.stepData.useConfetti] - Whether to show celebratory confetti animation
 * @param {boolean} [props.stepData.showUninstallButton] - Whether to show the extension uninstall button
 * @param {string} [props.stepData.uninstallButtonText] - Custom text for the uninstall button
 * @param {string} [props.stepData.uninstallingText] - Text to show during uninstallation
 * @param {string} [props.stepData.uninstalledText] - Text to show after successful uninstallation
 * @param {Function} props.setIsTaskComplete - Callback function to notify parent when task is complete
 * @returns {React.ReactElement} The rendered session completion component
 */
const SessionCompleted = ({ step, stepData, setIsTaskComplete }) => {
  const { getSessionStartData } = useAppStore();
  /**
   * @type {[boolean, Function]} Tracks if session data is being loaded
   * Used to display a loading indicator while fetching session data
   */
  const [isLoading, setIsLoading] = useState(true);
  
  /**
   * @type {[Array, Function]} Stores the BlockKit blocks to be rendered
   * These blocks define the structure and content of the completion message
   */
  const [contentBlocks, setContentBlocks] = useState([]);
  
  /** 
   * @type {[Object, Function]} Template replacements for dynamic content
   * Used to replace placeholders in the BlockKit content with actual values
   * @property {string} course - The course title from session data
   * @property {string} exam - The exam title from session data
   */
  const [replacements, setReplacements] = useState({});
  
  /** 
   * @type {[Object|null, Function]} Stores data about the current session
   * Contains information retrieved from the session API
   * @property {string} courseTitle - Title of the course
   * @property {string} examTitle - Title of the exam
   * @property {Object} [metadata] - Additional session metadata
   */
  const [sessionStartData, setSessionStartData] = useState(null);
  
  /**
   * Initializes session data by fetching from the API and setting up content blocks
   * This function handles the complete initialization process including:
   * - Fetching session data from the API
   * - Setting up template replacements for dynamic content
   * - Converting content to the appropriate BlockKit format
   * 
   * @async
   * @function initSessionData
   * @returns {Promise<void>}
   */
  const initSessionData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Fetch session start data which contains course and exam information
      const sessionStartData = await getSessionStartData();
      setSessionStartData(sessionStartData);

      // Set up template replacements for dynamic content
      // Provide fallbacks in case the session data is incomplete
      setReplacements({
        course: sessionStartData?.courseTitle || 'the course',
        exam: sessionStartData?.examTitle || 'the exam'
      });

      // Handle different content types for the body
      // The body can be either a string or a BlockKit JSON structure
      const body = stepData?.body;
      const isContentBlockString = body && typeof body === 'string';
      
      // Convert string content to BlockKit format if needed
      // If it's already in BlockKit format, use fromJson to parse it
      setContentBlocks(
        isContentBlockString 
          ? Blocks.Section().elements(Elements.Text().text(body))
          : Blocks.fromJson(body || [])
      );
    } catch (err) {
      // Log errors to console for debugging
      console.error('Failed to load session data:', err);
    } finally {
      // Always set loading to false when done, regardless of success or failure
      setIsLoading(false);
    }
  }, [stepData?.body]);

  /**
   * Effect hook to initialize session data when the component mounts
   * Uses the memoized initSessionData function to prevent unnecessary re-renders
   */
  useEffect(() => {
    // Call the initialization function when component mounts
    initSessionData();
    
    // Cleanup function in case component unmounts during data fetch
    return () => {
      // Any cleanup logic if needed when component unmounts
      // Currently no cleanup is needed, but this is a good practice
    };
  }, [initSessionData]);

  /**
   * Display a loading indicator while session data is being fetched
   * This prevents showing an incomplete or empty UI to the user
   */
  if (isLoading) {
    return <Loader />;
  }

  /**
   * Render the session completion UI with dynamic content and optional features
   */
  return (
    <SinglePane>
      {/* Main content container with accessibility attributes */}
      <Flex 
        as="div" 
        direction="column" 
        alignItems="center" 
        justifyContent="center" 
        height="100%"
        role="region"
        aria-live="polite"
        aria-label="Session Completed"
      >
        <View 
          as="div" 
          className="session-completed-content"
          width="90%"
          data-testid="session-completed-content"
        >
          {/* 
            BlockRenderer renders the content blocks with the provided replacements.
            When the wizard completes (if applicable), it notifies the parent component.
          */}
          <BlockRenderer 
            blocks={contentBlocks} 
            replacements={replacements}
            onWizardComplete={() => {
              // Notify parent that the completion flow is done
              setIsTaskComplete(true);
            }} 
          />
         
          {/* Optional extension uninstall button */}
          {stepData?.showUninstallButton && (   
            <Flex 
              as="div" 
              direction="column" 
              alignItems='center' 
              margin='x-large 0 0 0'
              data-testid="uninstall-button-container"
            >
              <UninstallButton 
                buttonText={stepData?.uninstallButtonText}
                uninstallingText={stepData?.uninstallingText}
                uninstalledText={stepData?.uninstalledText}
              />
            </Flex>
          )}
        </View>
      </Flex>
    </SinglePane>
  );
};

/**
 * Memoize the SessionCompleted component to prevent unnecessary re-renders
 * This is especially important for this component as it may contain complex UI elements
 * like confetti animations that could be performance-intensive
 */
const MemoizedSessionCompleted = React.memo(SessionCompleted);

export default MemoizedSessionCompleted;
